import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import AddIcon from '@mui/icons-material/Add';
import { differenceInMinutes, set } from 'date-fns';

import { Mitarbeiter, Projekt, ZeiteintragEingabe } from '../../types/zeiterfassung';
import { alsApiDatum, alsApiZeit, formatStunden } from '../../utils/format';

type Props = {
  projekte: Projekt[];
  projekteLaden: boolean;
  mitarbeiter: Mitarbeiter[];
  /** Admin und Projektleiter dürfen für andere erfassen. */
  darfFuerAndereErfassen: boolean;
  onSpeichern: (eingabe: ZeiteintragEingabe) => Promise<boolean>;
};

const standardStart = () => set(new Date(), { hours: 8, minutes: 0, seconds: 0, milliseconds: 0 });
const standardEnde = () => set(new Date(), { hours: 16, minutes: 30, seconds: 0, milliseconds: 0 });

/**
 * Formular zum Anlegen eines Zeiteintrags.
 *
 * Die Validierung erfolgt inline über `helperText`; vorher wurde bei
 * fehlenden Feldern ein `alert()` angezeigt.
 */
const ZeiteintragFormular = ({
  projekte,
  projekteLaden,
  mitarbeiter,
  darfFuerAndereErfassen,
  onSpeichern
}: Props) => {
  const [datum, setDatum] = useState<Date | null>(new Date());
  const [projektId, setProjektId] = useState('');
  const [startZeit, setStartZeit] = useState<Date | null>(standardStart);
  const [endZeit, setEndZeit] = useState<Date | null>(standardEnde);
  const [pause, setPause] = useState(0);
  const [beschreibung, setBeschreibung] = useState('');
  const [fuerMitarbeiterId, setFuerMitarbeiterId] = useState('');
  const [speichert, setSpeichert] = useState(false);
  const [abgeschickt, setAbgeschickt] = useState(false);

  // Stunden ergeben sich aus Start, Ende und Pause und sind nicht direkt
  // eingebbar. Die endgültige Zahl berechnet zusätzlich der Server.
  const stunden = useMemo(() => {
    if (!startZeit || !endZeit) return 0;
    const minuten = differenceInMinutes(endZeit, startZeit);
    if (minuten <= 0) return 0;
    return Math.max(0, Math.round(((minuten - pause) / 60) * 100) / 100);
  }, [startZeit, endZeit, pause]);

  const fehler = useMemo(() => {
    const f: Record<string, string> = {};

    if (!datum) f.datum = 'Bitte ein Datum wählen';
    if (!projektId) f.projekt = 'Bitte ein Projekt wählen';
    if (!startZeit) f.start = 'Bitte eine Startzeit angeben';
    if (!endZeit) f.ende = 'Bitte eine Endzeit angeben';

    if (startZeit && endZeit && differenceInMinutes(endZeit, startZeit) <= 0) {
      f.ende = 'Die Endzeit muss nach der Startzeit liegen';
    }

    if (startZeit && endZeit && pause >= differenceInMinutes(endZeit, startZeit)) {
      f.pause = 'Die Pause ist länger als die Zeitspanne';
    }

    return f;
  }, [datum, projektId, startZeit, endZeit, pause]);

  const istGueltig = Object.keys(fehler).length === 0;

  // Ist nur ein Projekt vorhanden, direkt vorauswählen.
  useEffect(() => {
    if (!projektId && projekte.length === 1) {
      setProjektId(String(projekte[0].id));
    }
  }, [projekte, projektId]);

  const zeigeFehler = (feld: string) => (abgeschickt ? fehler[feld] : undefined);

  const zuruecksetzen = () => {
    setDatum(new Date());
    setProjektId(projekte.length === 1 ? String(projekte[0].id) : '');
    setStartZeit(standardStart());
    setEndZeit(standardEnde());
    setPause(0);
    setBeschreibung('');
    setFuerMitarbeiterId('');
    setAbgeschickt(false);
  };

  const absenden = async () => {
    setAbgeschickt(true);
    if (!istGueltig) return;

    const apiDatum = alsApiDatum(datum);
    const apiStart = alsApiZeit(startZeit);
    const apiEnde = alsApiZeit(endZeit);

    if (!apiDatum || !apiStart || !apiEnde) return;

    setSpeichert(true);
    try {
      const erfolgreich = await onSpeichern({
        date: apiDatum,
        projectId: Number(projektId),
        startTime: apiStart,
        endTime: apiEnde,
        breakDuration: pause,
        description: beschreibung,
        ...(darfFuerAndereErfassen && fuerMitarbeiterId
          ? { forUserId: Number(fuerMitarbeiterId) }
          : {})
      });

      if (erfolgreich) {
        zuruecksetzen();
      }
    } finally {
      setSpeichert(false);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Zeit erfassen
        </Typography>

        <Grid container spacing={2}>
          {darfFuerAndereErfassen && (
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel id="fuer-mitarbeiter-label">Mitarbeiter</InputLabel>
                <Select
                  labelId="fuer-mitarbeiter-label"
                  value={fuerMitarbeiterId}
                  onChange={(e: SelectChangeEvent) => setFuerMitarbeiterId(e.target.value)}
                  label="Mitarbeiter"
                >
                  <MenuItem value="">
                    <em>Für mich selbst</em>
                  </MenuItem>
                  {mitarbeiter.map(m => (
                    <MenuItem key={m.id} value={String(m.id)}>
                      {m.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} sm={6} md={3}>
            <DatePicker
              label="Datum"
              value={datum}
              onChange={setDatum}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  error: Boolean(zeigeFehler('datum')),
                  helperText: zeigeFehler('datum')
                }
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth required error={Boolean(zeigeFehler('projekt'))}>
              <InputLabel id="projekt-label">Projekt</InputLabel>
              <Select
                labelId="projekt-label"
                value={projektId}
                label="Projekt"
                onChange={(e: SelectChangeEvent) => setProjektId(e.target.value)}
                disabled={projekteLaden}
              >
                {projekte.map(projekt => (
                  <MenuItem key={projekt.id} value={String(projekt.id)}>
                    {projekt.name}
                  </MenuItem>
                ))}
              </Select>
              {zeigeFehler('projekt') && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {zeigeFehler('projekt')}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <TimePicker
              label="Startzeit"
              value={startZeit}
              onChange={setStartZeit}
              ampm={false}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  error: Boolean(zeigeFehler('start')),
                  helperText: zeigeFehler('start')
                }
              }}
            />
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <TimePicker
              label="Endzeit"
              value={endZeit}
              onChange={setEndZeit}
              ampm={false}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  error: Boolean(zeigeFehler('ende')),
                  helperText: zeigeFehler('ende')
                }
              }}
            />
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              label="Pause (Minuten)"
              type="number"
              value={pause}
              onChange={e => setPause(Math.max(0, parseInt(e.target.value, 10) || 0))}
              inputProps={{ min: 0, step: 5 }}
              error={Boolean(zeigeFehler('pause'))}
              helperText={zeigeFehler('pause')}
            />
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              label="Ergibt"
              value={formatStunden(stunden)}
              InputProps={{ readOnly: true }}
              helperText="Wird automatisch berechnet"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Beschreibung"
              multiline
              rows={2}
              value={beschreibung}
              onChange={e => setBeschreibung(e.target.value)}
              inputProps={{ maxLength: 2000 }}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={absenden}
                disabled={speichert}
                startIcon={speichert ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
              >
                Hinzufügen
              </Button>
              <Button onClick={zuruecksetzen} disabled={speichert}>
                Zurücksetzen
              </Button>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default ZeiteintragFormular;
